<template>
  <div class="saved-view-editor">
    <div class="_top">
      <icon-picker
        :icon="buildIcon(emoji, iconType.icon)"
        show-placeholder
        size="3.2rem"
        :icon-size="16"
        bordered
        :disabled="disabled"
        :show-icon-variants="false"
        :icon-types="[iconType.icon]"
        class="_emoji-picker"
        @change="setEmoji"
      />
      <m-text-field
        v-model:value="title"
        auto-focus
        :disabled="disabled"
        :placeholder="$t('savedViewSelector.addViewPlaceholder')"
        @keydown.enter="submit"
        @blur="submit"
      />
    </div>
    <m-dropdown
      block
      :disabled="layoutDisabled"
      :title="$t('savedViewEditor.layout')"
    >
      <m-card-item
        icon="layout"
        :disabled="layoutDisabled"
      >
        {{ $t('savedViewEditor.layout') }}
        <template #right>
          <div :class="['_layout-type', layoutDisabled ? '-disabled' : '']">
            {{ getViewName(type) }}
          </div>
          <div class="_icon">
            <m-icon
              v-if="!layoutDisabled"
              type="down"
              :color="$colors.grey.lighten1"
              size="11"
            />
          </div>
        </template>
      </m-card-item>
      <template #overlay>
        <m-card
          list
        >
          <m-card-item
            v-for="v in allowedViewTypes"
            :key="v"
            :icon="getViewIcon(v)"
            :disabled="v === viewType.tree && !canActivateGoalTree"
            align-top
            @click="type = v"
          >
            <div class="_inner">
              <div class="_title">
                {{ getViewName(v) }}
                <plan-tag
                  v-if="v === viewType.tree"
                  :feature-flag="featureFlag.goalTree"
                  :style="{ display: 'inline-flex' }"
                />
              </div>
              <div class="_sub-title">
                {{ getDescription(v) }}
              </div>
            </div>
            <template #right>
              <m-icon
                v-if="type === v"
                type="check"
              />
            </template>
          </m-card-item>
        </m-card>
      </template>
    </m-dropdown>
  </div>
</template>

<script>
import IconPicker from '@/components/IconPicker.vue';
import PlanTag from '@/components/plans/PlanTag.vue';
import useAccess from '@/composables/access/access';
import useDebounce from 'shared/composables/debounce';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { buildIcon } from 'shared/lib/icon';
import { featureFlag, iconType, viewType } from 'shared/constants.json';
import { iconByType } from '@/lib/saved-view/saved-view';

export default {
  name: 'SavedViewEditor',
  props: {
    entity: {
      type: Object,
      required: true,
    },
    allowedViewTypes: {
      type: Array,
      default: () => [viewType.default],
    },
    viewApplication: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  components: { PlanTag, IconPicker },
  setup() {
    const { accountHasFeature } = useAccess();
    const { debounce } = useDebounce({ onBeforeUnmountFlush: true });
    const { loggedInUser } = useLoggedInUser();
    return {
      debounce,
      featureFlag,
      accountHasFeature,
      loggedInUser,
    };
  },
  data() {
    return {
      title: '',
      emoji: null,
      type: null,
      viewType,
      iconType,
    };
  },
  computed: {
    layoutDisabled() {
      return this.disabled || this.allowedViewTypes.length === 1;
    },
    canActivateGoalTree() {
      return this.accountHasFeature([featureFlag.goalTree]);
    },
  },
  methods: {
    buildIcon,
    getViewIcon(value) {
      return iconByType(value);
    },
    getViewName(value) {
      return this.$t(`list.${value}`);
    },
    getDescription(value) {
      return this.$t(`savedViewSelector.${value}Description`);
    },
    setEmoji(emoji) {
      this.emoji = emoji.value;
    },
    submit() {
      if (this.title === this.entity.title) {
        return;
      }
      this.updateDebounced();
    },
    emitUpdate() {
      const view = {
        uid: this.entity.uid,
        isTemp: this.entity.isTemp,
        title: this.title,
        emoji: this.emoji,
        viewType: this.type,
      };

      this.$emit('update', view);
    },
    updateDebounced(wait = 0) {
      const fn = () => this.emitUpdate();
      this.debounce(fn, wait);
    },
  },
  watch: {
    title(newVal, oldVal) {
      if (newVal === oldVal || oldVal === '') {
        return;
      }
      this.updateDebounced(800);
    },
    emoji(newVal, oldVal) {
      if (newVal === oldVal || oldVal === null) {
        return;
      }
      this.updateDebounced();
    },
    type(newVal, oldVal) {
      if (newVal === oldVal || oldVal === null) {
        return;
      }
      this.updateDebounced();
    },
  },
  created() {
    this.title = this.entity.title;
    this.emoji = this.entity.emoji;
    this.type = this.entity.viewType;
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  .saved-view-editor {
    ._top {
      display: flex;
      padding: 1.2rem;

      ._emoji-picker {
        margin-right: .8rem;
      }
    }

    ._layout-type {
      color: $font-color-secondary;

      &.-disabled {
        color: $font-color-tertiary;
      }
    }

    ._icon {
      margin-left: .4rem;
    }
  }

._inner {
  ._title {
    height: 2.1rem;
  }

  ._sub-title {
    max-width: 21rem;
    font-size: $font-size-2;
    color: $font-color-tertiary;
    white-space: normal;
  }
}

</style>
